import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  Download,
  FileText,
  Info,
  Loader2,
  Maximize2,
  Minus,
  Plus,
} from "lucide-react"
import { useEffect, useRef, useState, type ReactNode } from "react"

/** Right-side PDF preview panel for the Conformity editor.
 *
 *  Mirrors the supplier-inquiry DraftPreviewPanel:
 *    - Filename + FileText icon on the LEFT of the toolbar
 *    - Zoom −/%/+ and Download on the RIGHT
 *    - Iframe fills the flex area with `zoom:` CSS property applied
 *    - Small "live render" hint at the bottom
 *
 *  Zoom state lives internally — the parent doesn't care what magnification
 *  the reviewer is looking at. Everything else (HTML, error state, download
 *  handler) is prop-driven so the panel stays presentational.
 */
export function ConformityPreviewPanel({
  html,
  isLoading,
  isRefreshing = false,
  isError,
  keyId,
  onDownload,
  downloadPending = false,
  className = "flex w-1/2 min-w-0 flex-col",
  containerRef,
  hideHint = false,
  label,
  headerExtra,
  fitToWidth = false,
  showDownload = true,
  isRefetching = false,
  onPreviewPdf,
  previewPdfLabel,
  scrollToBottomOnLoad = false,
  onScrolledToBottom,
}: {
  html: string | undefined
  isLoading: boolean
  /** A background refetch of an already-loaded preview (e.g. after signing
   *  or unsigning) — the existing PDF stays on screen, this only adds a
   *  quiet inline hint next to the title rather than blanking the panel. */
  isRefreshing?: boolean
  isError: boolean
  /** Iframe `key` — forces a re-mount when the declaration id changes so
   *  the srcDoc doesn't blend across rows. */
  keyId: string
  onDownload: () => void
  downloadPending?: boolean
  /** Root classes. v1 is a direct child of the body split (hence the default
   *  half width); v2 nests it inside its own document section and passes a
   *  full-width, stretching box instead. */
  className?: string
  /** v2 reaches the iframe through this for the hover-highlight. */
  containerRef?: React.Ref<HTMLDivElement>
  /** v2 renders its own live-render hint under the pane. */
  hideHint?: boolean
  /** Toolbar caption. v2 passes the document's own title in its language;
   *  the default is the v1 label. */
  label?: string
  /** Beside the title in the toolbar (the v2 editor's template switcher). */
  headerExtra?: ReactNode
  /** Start at the zoom that makes A4 fill the pane instead of a fixed 72%. */
  fitToWidth?: boolean
  /** v2 drafts offer "Preview PDF" in the footer — one affordance is enough. */
  showDownload?: boolean
  /** A background refetch: keep the rendered page, spin in the toolbar. */
  isRefetching?: boolean
  /** Opens the rendered PDF full screen — the action belongs next to the
   *  document, not in the evaluation footer. */
  onPreviewPdf?: () => void
  previewPdfLabel?: string
  /** One-shot: the next time the iframe actually finishes loading new
   *  content, scroll it to the bottom (the signature sits in the `.sigrow`
   *  block near the footer) instead of leaving the reviewer looking at
   *  wherever they'd previously scrolled. Not tied to every reload — the
   *  caller arms this only right after a signature change, since yanking the
   *  scroll position on an unrelated edit (cover note, recipients) would be
   *  disorienting. */
  scrollToBottomOnLoad?: boolean
  /** Consumes the one-shot above so a LATER, unrelated reload doesn't scroll
   *  again. */
  onScrolledToBottom?: () => void
}) {
  const { translate } = useTranslation()
  // 72% matches the inquiry composer's default so both surfaces feel identical.
  const [zoom, setZoom] = useState(72)
  const frameRef = useRef<HTMLIFrameElement>(null)
  // Set once the user works the zoom buttons: from then on the fit stops
  // tracking the pane, so a later resize cannot throw away their choice.
  const userZoomed = useRef(false)

  // A4 is 210mm ≈ 794px at 96dpi, minus a little breathing room.
  //
  // This re-fits on every pane resize rather than latching after the first
  // measurement. The editor's document pane is draggable, and a fit computed
  // once for the default split kept a stale `zoom` after any drag: the frame
  // is scaled with the CSS `zoom` property, so a width the zoom no longer
  // matches leaves the preview mis-scaled with no way back except
  // double-clicking the handle — which "fixed" it only because that resets
  // the split to exactly the width the one-shot fit was computed for.
  useEffect(() => {
    const box = frameRef.current?.parentElement
    if (!fitToWidth || !box) return
    const fit = (width: number) => {
      if (userZoomed.current || !width) return
      setZoom(Math.min(150, Math.max(30, Math.round((width / 794) * 100) - 2)))
    }
    fit(box.clientWidth)
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(([entry]) =>
      fit(entry.contentRect.width),
    )
    observer.observe(box)
    return () => observer.disconnect()
  }, [fitToWidth, html])

  return (
    <div ref={containerRef} className={className}>
      <div className="flex items-center gap-3 border-b border-grey4 px-4 py-2">
        <span className="flex min-w-0 items-center gap-1.5 text-[12px] text-grey5">
          <FileText size={12} className="shrink-0" />
          <span className="truncate">
            {label ?? translate("MC_CS_CONF_EDIT_PREVIEW_LABEL")}
          </span>
          {isRefetching && (
            <Loader2 size={11} className="shrink-0 animate-spin" />
          )}
        </span>
        {headerExtra}
        {isRefreshing && (
          <span className="flex items-center gap-1 text-[11px] text-grey5">
            <Loader2 size={11} className="animate-spin" />
            {translate("MC_CS_CONF_EDIT_PREVIEW_REFRESHING")}
          </span>
        )}
        <span className="flex-1" />
        <div className="flex items-center gap-1.5">
          {onPreviewPdf && (
            <button
              type="button"
              onClick={onPreviewPdf}
              className="mr-2 inline-flex items-center gap-1.5 rounded-md border border-yellow1/60 bg-yellow1/10 px-2.5 py-1 text-[12px] font-semibold text-yellow1 transition-colors hover:bg-yellow1/20"
            >
              <Maximize2 size={12} />
              {previewPdfLabel}
            </button>
          )}
          <button
            type="button"
            aria-label={translate("MC_CS_CONF_EDIT_ARIA_ZOOM_OUT")}
            onClick={() => {
              userZoomed.current = true
              setZoom((z) => Math.max(30, z - 10))
            }}
            className="text-grey5 hover:text-white"
          >
            <Minus size={14} />
          </button>
          <span className="font-mono text-[11px] text-grey5">{zoom}%</span>
          <button
            type="button"
            aria-label={translate("MC_CS_CONF_EDIT_ARIA_ZOOM_IN")}
            onClick={() => {
              userZoomed.current = true
              setZoom((z) => Math.min(150, z + 10))
            }}
            className="text-grey5 hover:text-white"
          >
            <Plus size={14} />
          </button>
          {showDownload && (
            <button
              type="button"
              aria-label={translate("MC_CS_CONF_EDIT_ARIA_DOWNLOAD_PDF")}
              onClick={onDownload}
              disabled={downloadPending}
              className="ml-2 text-grey5 hover:text-white disabled:opacity-50"
            >
              {downloadPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
            </button>
          )}
        </div>
      </div>
      {isLoading && !html ? (
        <div className="flex min-h-0 flex-1 items-center justify-center bg-white text-[12px] text-grey5">
          <Loader2 size={14} className="mr-2 animate-spin" />
          {translate("MC_CS_CONF_EDIT_RENDERING")}
        </div>
      ) : isError || !html ? (
        <div className="flex min-h-0 flex-1 items-center justify-center bg-white p-6 text-center text-[12px] text-destructive">
          {translate("MC_CS_CONF_EDIT_PREVIEW_FAILED")}
        </div>
      ) : (
        // Zoom via the non-standard `zoom` CSS property — Chromium + Safari
        // support it directly on iframes; Firefox degrades to 100% which is
        // fine for a preview. No transform gymnastics that would break the
        // iframe's own layout (learned that the hard way).
        <iframe
          key={keyId}
          ref={frameRef}
          title={translate("MC_CS_CONF_EDIT_PREVIEW_IFRAME_TITLE")}
          srcDoc={html}
          // allow-same-origin (only) is required for the v2 editor's
          // hover-highlight: it reads this iframe's `contentDocument` to
          // toggle a class on the hovered framework's block
          // (ConformityEditorModuleV2's `blockFor`/`docPaneRef`), which a
          // default opaque-origin srcdoc always reports as null. It is safe
          // here because `allow-scripts` is deliberately absent — the frame
          // cannot run any script, so it cannot act on its own DOM access —
          // and forms/top-level navigation stay blocked; the HTML itself is
          // server-rendered from our own copy, never third-party content.
          sandbox="allow-same-origin"
          className="min-h-0 flex-1 bg-white"
          style={{ zoom: `${zoom}%` }}
          onLoad={() => {
            if (!scrollToBottomOnLoad) return
            const win = frameRef.current?.contentWindow
            const body = frameRef.current?.contentDocument?.body
            if (win && body) win.scrollTo(0, body.scrollHeight)
            onScrolledToBottom?.()
          }}
        />
      )}
      {!hideHint && (
        <div className="inline-flex items-center gap-1.5 border-t border-grey4 bg-grey2 px-4 py-1.5 text-[10.5px] text-grey5">
          <Info size={11} />
          {translate("MC_CS_CONF_EDIT_LIVE_RENDER_HINT")}
        </div>
      )}
    </div>
  )
}
